/**
 * Customer Journey model — surfaced THROUGH HubSpot (Brian's call).
 *
 * Per-account = one HubSpot DEAL. The journey is derived from the deal's
 * `dealstage` + `sr_*` properties, read-only, via the JWT backend
 * (api.srchamp.com). We mirror the canonical model from the real
 * sr-customer-journey-backend — the 7-group approval journey and the 14-stage
 * production tracker — but reconstruct progress from the deal's stage (which
 * that backend advances: `approvals_<group>_complete`, `production_met`,
 * `closedwon`). The per-signature detail lives in the journey backend's own DB;
 * here we show what the deal encodes.
 *
 * BACKEND DEPENDENCY (flagged): sr-portal-backend does not yet expose a deals
 * read endpoint. `api.deals` targets `/api/hubspot/deals` — add that route (or
 * point it at the journey backend) to go live. `?demo=1` drives the full model.
 */

import { normalizeAccounts } from './clickup.js'

/* ---- Canonical models (from the real source) ----------------------- */

// 7-group approval journey (order → … → delivery).
export const APPROVAL_GROUPS = [
  { key: 'order', num: 1, title: 'Order Confirmation', owner: 'SR' },
  { key: 'roster', num: 2, title: 'Roster Submission', owner: 'Customer' },
  { key: 'design', num: 3, title: 'Design Approval', owner: 'Customer' },
  { key: 'licensing', num: 4, title: 'Licensing Approval', owner: 'Third party' },
  { key: 'jcad', num: 5, title: 'JCAD Approval', owner: 'Customer' },
  { key: 'finance', num: 6, title: 'Finance Approval', owner: 'Customer' },
  { key: 'delivery', num: 7, title: 'Delivery Approval', owner: 'Customer' },
]

// 14-stage production tracker (post-release).
export const PRODUCTION_STAGES = [
  { key: 'package_confirmed', n: 1, title: 'Manufacturing Package Confirmed', d0: 0 },
  { key: 'model_verified', n: 2, title: 'Production Model Verified', d0: 3 },
  { key: 'model_printed', n: 3, title: 'Wax / Resin Model Printed', d0: 8 },
  { key: 'casting_tree', n: 4, title: 'Casting Tree Prepared', d0: 15 },
  { key: 'mold_burnout', n: 5, title: 'Mold & Burnout Completed', d0: 21 },
  { key: 'metal_cast', n: 6, title: 'Metal Casting', d0: 29 },
  { key: 'raw_cleanup', n: 7, title: 'Raw Casting Cleanup', d0: 36 },
  { key: 'sizing_correction', n: 8, title: 'Sizing & Structural Correction', d0: 43 },
  { key: 'surface_prep', n: 9, title: 'Surface Preparation & Burnishing', d0: 50 },
  { key: 'detail_refinement', n: 10, title: 'Detail Refinement', d0: 59 },
  { key: 'stones_set', n: 11, title: 'Stones Prepared & Set', d0: 68 },
  { key: 'engrave_finish', n: 12, title: 'Engraving, Enamel, Plating & Finish', d0: 79 },
  { key: 'final_qc', n: 13, title: 'Final Quality Control', d0: 92 },
  { key: 'packaging', n: 14, title: 'Packaging & Delivery Prep', d0: 103 },
]

// High-level phases for the funnel (scan view).
export const JOURNEY_PHASES = [
  { key: 'new', label: 'New', blurb: 'Order received', tone: 'info' },
  { key: 'approvals', label: 'Approvals', blurb: '7-group sign-off', tone: 'warn' },
  { key: 'production', label: 'Production', blurb: '14-stage build', tone: 'brass' },
  { key: 'delivery', label: 'Delivery', blurb: 'Shipping', tone: 'info' },
  { key: 'complete', label: 'Complete', blurb: 'Delivered & closed', tone: 'ok' },
]
export const HOLD = 'hold'
// Off-pipeline bucket for a live order whose raw status doesn't map to a phase.
// It exists so an unrecognized status is shown honestly instead of being
// silently filed under "New" (a fabricated phase) — it is NOT a funnel stage.
export const UNMAPPED = 'unmapped'
const OFF_PIPELINE = [
  { key: HOLD, label: 'On hold', tone: 'danger' },
  { key: UNMAPPED, label: 'Unmapped status', tone: 'muted' },
]
export const PHASE_LABEL = Object.fromEntries([...JOURNEY_PHASES, ...OFF_PIPELINE].map((p) => [p.key, p.label]))
export const PHASE_TONE = Object.fromEntries([...JOURNEY_PHASES, ...OFF_PIPELINE].map((p) => [p.key, p.tone || 'danger']))

/* ---- Deal → journey derivation ------------------------------------- */

const props = (r) => (r && typeof r.properties === 'object' ? r.properties : {})
function field(record, keys) {
  const p = props(record)
  for (const k of keys) {
    if (record?.[k] !== undefined && record[k] !== null && record[k] !== '') return record[k]
    if (p?.[k] !== undefined && p[k] !== null && p[k] !== '') return p[k]
  }
  return undefined
}
function parseDate(v) {
  if (v == null || v === '') return null
  if (typeof v === 'number') return new Date(v)
  const s = String(v).trim()
  if (/^\d{10,}$/.test(s)) return new Date(Number(s))
  const d = new Date(s.length === 10 ? s + 'T00:00:00' : s)
  return Number.isNaN(d.getTime()) ? null : d
}
const num = (v) => { if (v == null || v === '') return null; const n = Number(String(v).replace(/[^0-9.-]/g, '')); return Number.isFinite(n) ? n : null }

/**
 * Classify a HubSpot dealstage → { phase, approvalsDone, complete }.
 *
 * ⚠️ `approvalsDone` IS ALWAYS EMPTY, AND THAT IS THE FIX (2026-09-18).
 *
 * This used to mark all seven approval groups done whenever the stage read
 * production or complete, and every group up to N done when it read
 * `approvals_<N>_complete`. Nothing is STORED behind either: a live account
 * showing seven green checks returned an empty approvals array, and roughly
 * 2,833 accounts displayed customer approvals that no customer could have
 * given, since zero coach credentials are active.
 *
 * A stage word is not a signature. A blank is truthful; a false check is
 * evidence of something that did not happen. So the phase is still read off
 * the stage (that is what the stage IS), and approval state is never derived
 * from it. It will come from the approvals store, per group, when that exists.
 */
export function classifyDealStage(dealstage) {
  const s = String(dealstage || '').toLowerCase()
  const approvalsDone = new Set()

  const m = s.match(/approvals?_([a-z]+)_complete/)
  if (/\b(closed\s*lost|cancel|void)\b/.test(s)) return { phase: HOLD, approvalsDone, complete: false }
  if (/\b(closed\s*won|delivered|complete|fulfilled)\b/.test(s)) {
    return { phase: 'complete', approvalsDone, complete: true }
  }
  if (/\b(ship|out for delivery|delivery)\b/.test(s)) return { phase: 'delivery', approvalsDone, complete: false }
  if (/\b(production_met|production-met|production|manufactur|in progress)\b/.test(s)) {
    return { phase: 'production', approvalsDone, complete: false }
  }
  if (m || /\b(approval|qualifiedtobuy|presentationscheduled|review)\b/.test(s)) return { phase: 'approvals', approvalsDone, complete: false }
  return { phase: 'new', approvalsDone, complete: false }
}

/** Current approval group = first not-yet-complete group. */
export function currentApprovalGroup(approvalsDone) {
  for (const g of APPROVAL_GROUPS) if (!approvalsDone.has(g.key)) return g
  return null
}

export function normalizeDeal(record) {
  if (!record || typeof record !== 'object') return null
  const id = field(record, ['id', 'hs_object_id', 'dealId'])
  const dealstage = field(record, ['dealstage', 'sr_deal_stage', 'stage']) || ''
  const cls = classifyDealStage(dealstage)
  const mfgReleaseAt = parseDate(field(record, ['sr_mfg_release_at', 'mfg_release_at']))

  const deal = {
    id: id != null ? String(id) : String(field(record, ['dealname']) || Math.random()),
    org: field(record, ['sr_org', 'org', 'dealname', 'sr_account', 'account']) || 'Untitled account',
    orderNo: field(record, ['sr_order_no', 'order_no']) || '',
    dealNo: field(record, ['sr_deal_no', 'deal_no']) || '',
    sport: field(record, ['sr_sport', 'sport']) || '',
    qty: num(field(record, ['sr_qty', 'qty', 'sr_quantity'])),
    amount: num(field(record, ['amount', 'sr_amount', 'deal_value'])),
    owner: field(record, ['sr_rep_primary', 'sr_assigned_rep', 'hubspot_owner', 'owner']) || 'Unassigned',
    dealstageRaw: String(dealstage),
    phase: cls.phase,
    approvalsDone: cls.approvalsDone,
    complete: cls.complete,
    mfgReleaseAt,
    // This feed reports a broad phase, not observed manufacturing milestones.
    // Elapsed days and a release date cannot prove casting, stones or QC.
    productionStage: null,
    atRisk: !!field(record, ['sr_at_risk', 'at_risk']),
    closeDate: parseDate(field(record, ['closedate', 'sr_close_date'])),
    // No stored approval sits behind a deal stage. See classifyDealStage.
    approvalsKnown: false,
    source: 'hubspot-deals',
    raw: record,
  }
  /* No "Awaiting: <group>" either. With nothing stored, the first group in
   * the list is not the one being waited on; it is just the first group. */
  deal.currentGroup = null
  return deal
}

export function normalizeDeals(response) {
  const list = Array.isArray(response)
    ? response
    : response?.deals || response?.results || response?.records || response?.data || response?.accounts || []
  return list.map(normalizeDeal).filter(Boolean)
}

/* ---- ClickUp order → journey (the primary source) ------------------- */

/**
 * The Customer Journey's source of truth is the REAL order tasks in ClickUp
 * list 901000306889 (assignees = real reps), brokered by the backend. Those
 * come back as ClickUp tasks, so they are normalized by `lib/clickup.js` and
 * then mapped onto the SAME journey shape the board/detail already consume —
 * one view, either source, no component changes.
 *
 * ClickUp's canonical order stages (draft/design → proof/approval → submitted →
 * production → fulfillment → complete) collapse onto the high-level phases:
 */
const CLICKUP_PHASE = {
  new: 'new',
  design: 'approvals',
  approval: 'approvals',
  production: 'production',
  fulfillment: 'delivery',
  complete: 'complete',
  hold: HOLD,
  other: UNMAPPED,
}

/* The backend's phase vocabulary → this page's phase keys.
 *
 * The backend answers in Title Case off the list's configured statuses; the
 * funnel here keys on lowercase. `Lost` has no funnel column and deliberately
 * does not get forced into one — 373 orders are in it, and quietly filing them
 * under `hold` or `complete` would misreport the business in exactly the way
 * this mapping is meant to stop. They carry an honest `lost` key until there
 * is a decision about how the funnel should show them. */
const BACKEND_PHASE = {
  new: 'new',
  approvals: 'approvals',
  production: 'production',
  delivery: 'delivery',
  complete: 'complete',
  lost: 'lost',
  hold: HOLD,
}

/* "Needs attention", taken from the backend, which already decides it —
 * overdue and still open, blocked, or unowned.
 *
 * THE TWO SHAPES ARE NOT THE SAME TYPE, and that is the trap. An ORDER carries
 * `attention` as a boolean; an ACCOUNT carries it as a COUNT of its orders
 * that need attention. A single `=== true` test silently reads every account
 * as fine, which is how the page came to show "Needs attention 0" while
 * accounts were genuinely flagged.
 *
 * The local fallback only runs when the feed carries neither. */
function attentionFrom(acct, complete) {
  const a = acct?.raw?.attention
  if (typeof a === 'boolean') return a
  if (typeof a === 'number') return a > 0
  return !!(acct?.dueDate && !complete && acct.dueDate < new Date())
}

export function fromClickUpOrder(acct) {
  if (!acct) return null
  // The backend's classification wins. It reads the list's OWN configured
  // statuses and maps every one of them; the local `stage` regex is a fallback
  // for a feed that has not been through that classifier.
  const fromBackend = acct.phase ? BACKEND_PHASE[String(acct.phase).toLowerCase()] : null
  const phase = fromBackend || CLICKUP_PHASE[acct.stage] || UNMAPPED
  const complete = phase === 'complete'

  /* ⚠️ NO BLANKET. This marked all seven groups signed for any order in
   * production, delivery or complete. An order reaching production proves SR
   * released it, not that a customer signed five customer approvals, and no
   * approval is stored for any of them. Every group is UNKNOWN until a real
   * approval record says otherwise. See classifyDealStage. */
  const approvalsDone = new Set()

  const deal = {
    id: acct.id,
    org: acct.account || acct.order || 'Untitled account',
    orderNo: acct.account ? acct.order : '',
    dealNo: '',
    sport: '',
    qty: null,
    amount: typeof acct.value === 'number' ? acct.value : null,
    owner: acct.owner || 'Unassigned',
    /* Every rep on the account, not just the first. Madison Gill sits on 340 of
     * Philip's orders as an ops overlay, so "the owner" and "who is on it" are
     * genuinely different questions and a single name answers only one. */
    owners: Array.isArray(acct.owners) && acct.owners.length ? acct.owners : (acct.owner ? [acct.owner] : []),
    orders: Array.isArray(acct.orders) ? acct.orders : [],
    orderCount: acct.orderCount || (Array.isArray(acct.orders) ? acct.orders.length : 0),
    phaseCounts: acct.counts || null,
    attention: acct.attention || 0,
    dealstageRaw: acct.statusRaw || '',
    phase,
    approvalsDone,
    approvalsKnown: false,
    complete,
    mfgReleaseAt: null,
    productionStage: null,
    /* The backend already decides "needs attention" — overdue AND still open,
     * blocked, or unowned — and ships it per order. Prefer it.
     *
     * The local test read `acct.stage`, which the order feed does not carry at
     * all, so it was comparing undefined to 'complete' on every row: an order
     * that was closed months ago still counted as at risk if it had a due
     * date, and the page's own "Needs attention" tile read 0. A flag that is
     * wrong in both directions is worse than no flag, because people act on it. */
    atRisk: attentionFrom(acct, complete),
    closeDate: acct.dueDate || null,
    dueDate: acct.dueDate || null,
    updatedAt: acct.updatedAt || null,
    url: acct.url || '',
    source: 'clickup',
    raw: acct.raw,
  }
  deal.currentGroup = null // per-group state is not in the order feed
  return deal
}

/** Raw ClickUp order-feed response → journey deals. */
export function normalizeOrders(response) {
  return normalizeAccounts(response).map(fromClickUpOrder).filter(Boolean)
}

/* ---- Aggregation --------------------------------------------------- */

export function countByPhase(deals) {
  const c = {}
  for (const d of deals) c[d.phase] = (c[d.phase] || 0) + 1
  return c
}

export function filterDeals(deals, { phase, query, rep } = {}) {
  const q = String(query || '').trim().toLowerCase()
  const r = String(rep || '').trim().toLowerCase()
  return deals.filter((d) => {
    if (phase && d.phase !== phase) return false
    /* Matches ANY rep on the account, not just the first name shown. Filtering
     * on the displayed owner alone would hide every account where someone is
     * the second assignee — which is most of one desk's book. */
    if (r) {
      if (r === UNASSIGNED_KEY) {
        if ((d.owners || []).length) return false
      } else if (!(d.owners || []).some((o) => String(o).toLowerCase() === r)) {
        return false
      }
    }
    if (q && !`${d.org} ${d.orderNo} ${(d.owners || []).join(' ')} ${d.sport} ${d.dealstageRaw}`.toLowerCase().includes(q)) return false
    return true
  })
}

export const UNASSIGNED_KEY = '(unassigned)'

/**
 * THE BOOK, BY REP.
 *
 * Brian: "we've got to be able to track by sales rep."
 *
 * Counts every account each rep is on — accounts, orders, how many need
 * attention, and the phase spread. An account with two reps counts for both,
 * because both are working it; summing this column will therefore exceed the
 * account total, and that is correct rather than a bug. `accountsTotal` is the
 * number to trust for "how big is the book".
 */
export function repRollup(deals) {
  const byRep = new Map()
  const touch = (name) => {
    if (!byRep.has(name)) {
      byRep.set(name, {
        rep: name, accounts: 0, orders: 0, attention: 0,
        phases: {}, unassigned: name === UNASSIGNED_KEY,
      })
    }
    return byRep.get(name)
  }

  for (const d of deals) {
    const names = (d.owners || []).length ? d.owners : [UNASSIGNED_KEY]
    for (const n of names) {
      const r = touch(String(n))
      r.accounts += 1
      r.orders += d.orderCount || 1
      r.attention += d.attention || (d.atRisk ? 1 : 0)
      r.phases[d.phase] = (r.phases[d.phase] || 0) + 1
    }
  }

  return [...byRep.values()].sort((a, b) => {
    // Unassigned last: it is a gap to close, not a person to rank.
    if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1
    return b.accounts - a.accounts
  })
}

/** Progress ratio 0–1 across the high-level phases (hold = null). */
export function phaseProgress(phaseKey) {
  const idx = JOURNEY_PHASES.findIndex((p) => p.key === phaseKey)
  return idx === -1 ? null : (idx + 1) / JOURNEY_PHASES.length
}

export function formatMoney(n) {
  if (n == null) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

